import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Check, ShoppingBag, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Container, Section, SectionHeading } from '@/components/site/Section';
import { ProductCard, PriceTag } from '@/components/site/ProductCard';
import { QuantityStepper } from '@/components/site/CartSheet';
import { useCatalog } from '@/state/catalog';
import { useCart } from '@/state/cart';
import { formatKr } from '@/lib/api';
import { productImage } from '@/lib/images';
import { Picture } from '@/components/site/Picture';

export default function ProductDetailPage() {
  const { slug } = useParams();
  const catalog = useCatalog();
  const cart = useCart();
  const navigate = useNavigate();
  const product = catalog.bySlug[slug];
  const [qty, setQty] = useState(10);

  useEffect(() => {
    if (product) document.title = `${product.name} · Hardanger Rock`;
  }, [product]);

  if (catalog.status === 'loading') {
    return (
      <Container className="grid gap-10 py-12 lg:grid-cols-2" aria-busy="true" data-testid="product-loading">
        <Skeleton className="aspect-square w-full rounded-[var(--hr-radius-lg)]" />
        <div className="space-y-4 pt-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-24 w-full" />
        </div>
      </Container>
    );
  }

  if (catalog.status === 'ready' && !product) {
    return (
      <Container className="py-24 text-center" data-testid="product-not-found">
        <h1 className="font-display text-[28px] font-semibold text-[var(--hr-heading)]">Fant ikke produktet</h1>
        <p className="mt-2 text-[var(--hr-muted)]">Produktet finnes ikke eller er tatt ut av sortimentet.</p>
        <Button asChild className="mt-6 h-11 rounded-[10px]">
          <Link to="/produkter">Til produkter</Link>
        </Button>
      </Container>
    );
  }
  if (!product) return null;

  const isQuote = product.priceType === 'quote';
  const related = catalog.products.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 4);
  const catLabel = catalog.categories.find((c) => c.id === product.category)?.label ?? product.category;
  const estimate = !isQuote && Number(qty) > 0 ? product.pricePerTonne * Number(qty) : null;

  const addToCart = () => {
    const q = Number(qty);
    if (!q || q <= 0) {
      toast.message('Oppgi mengde i tonn');
      return;
    }
    cart.add(product.slug, q);
    toast.success(`${product.name} lagt til`, { description: `${q} tonn · ${formatKr(product.pricePerTonne * q)} eks. mva`, action: { label: 'Vis bestilling', onClick: () => cart.setOpen(true) } });
    cart.setOpen(true);
  };

  return (
    <div data-testid="product-page">
      <Container className="pt-8">
        <nav aria-label="Brødsmuler" className="text-[13px] text-[var(--hr-muted)]" data-testid="product-breadcrumbs">
          <Link to="/" className="hover:text-[var(--hr-heading)]">
            Forsiden
          </Link>
          <span className="mx-2 text-[var(--hr-muted-2)]">/</span>
          <Link to="/produkter" className="hover:text-[var(--hr-heading)]">
            Produkter
          </Link>
          <span className="mx-2 text-[var(--hr-muted-2)]">/</span>
          <Link to={`/produkter?kategori=${product.category}`} className="hover:text-[var(--hr-heading)]">
            {catLabel}
          </Link>
          <span className="mx-2 text-[var(--hr-muted-2)]">/</span>
          <span className="text-[var(--hr-text)]">{product.name}</span>
        </nav>
      </Container>

      <Container className="grid gap-10 py-8 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:py-12">
        <div className="relative">
          <Picture variant={productImage(product.image)} sizes="(min-width: 1024px) 48vw, 100vw" priority alt={product.name} className="aspect-square w-full rounded-[var(--hr-radius-lg)] object-cover shadow-[var(--hr-shadow-sm)]" data-testid="product-image" />
          <span className="absolute left-4 top-4 inline-flex h-8 items-center rounded-full bg-white/90 px-3 text-[13px] font-medium text-[var(--hr-heading)] shadow-[var(--hr-shadow-xs)] backdrop-blur">{product.fraction}</span>
        </div>

        <div className="lg:pt-2">
          <div className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--hr-accent-text)]">{catLabel}</div>
          <h1 className="mt-2 font-display text-[34px] font-semibold leading-[1.05] tracking-[-0.02em] text-[var(--hr-heading)] sm:text-[42px]" data-testid="product-title">
            {product.name}
          </h1>
          <p className="mt-4 text-[16px] leading-7 text-[var(--hr-text)]" data-testid="product-description">{product.description}</p>

          <ul className="mt-6 flex flex-wrap gap-2" aria-label="Bruksområder">
            {product.uses.map((u) => (
              <li key={u} className="inline-flex h-8 items-center gap-1.5 rounded-full border border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] px-3 text-[13px] font-medium text-[var(--hr-text)]">
                <Check className="h-3.5 w-3.5 text-[var(--hr-green)]" aria-hidden="true" /> {u}
              </li>
            ))}
          </ul>

          <div className="hr-surface mt-8 p-6" data-testid="order-box">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">{isQuote ? 'Pris' : 'Pris pr. tonn'}</div>
                <PriceTag product={product} size="lg" className="mt-1" />
              </div>
              {!isQuote && <div className="text-[12.5px] text-[var(--hr-muted)]">Netto pr. 10 dager · frakt avtales</div>}
            </div>

            {isQuote ? (
              <div className="mt-5">
                <p className="text-[14.5px] leading-6 text-[var(--hr-muted)]">Dette produktet prises per prosjekt ut fra mengde, størrelse og leveringssted. Send en kort forespørsel, så kommer vi tilbake med pris og leveringsplan.</p>
                <Button className="mt-5 h-12 w-full rounded-[12px] bg-[var(--site-orange)] text-[15px] font-semibold text-[var(--site-ink)] shadow-none hover:bg-[var(--site-orange-2)]" onClick={() => navigate(`/prosjekt?produkt=${product.slug}`)} data-testid="request-quote-button">
                  Be om prosjektpris <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            ) : (
              <div className="mt-5">
                <label htmlFor="product-qty-input" className="text-[13px] font-medium text-[var(--hr-text)]">
                  Mengde (tonn)
                </label>
                <div className="mt-2 flex flex-wrap items-center gap-4">
                  <QuantityStepper value={qty} onChange={setQty} step={5} min={1} size="lg" testId="product-qty" />
                  <div className="min-w-[140px]">
                    <div className="text-[12px] text-[var(--hr-muted)]">Estimert eks. mva</div>
                    <div className="hr-tabular font-display text-[22px] font-semibold leading-tight text-[var(--hr-heading)]" data-testid="product-estimate">{estimate !== null ? formatKr(estimate) : '–'}</div>
                  </div>
                </div>
                <Button className="mt-5 h-12 w-full rounded-[12px] bg-[var(--site-orange)] text-[15px] font-semibold text-[var(--site-ink)] shadow-none hover:bg-[var(--site-orange-2)]" onClick={addToCart} data-testid="add-to-cart-button">
                  <ShoppingBag className="mr-2 h-4 w-4" aria-hidden="true" /> Legg i bestilling
                </Button>
                <p className="mt-3 flex items-center gap-2 text-[12.5px] text-[var(--hr-muted)]">
                  <Truck className="h-4 w-4" aria-hidden="true" /> Hent selv, tippbil, big bag med kranbil eller båt – velges i neste steg.
                </p>
              </div>
            )}
          </div>
        </div>
      </Container>

      {related.length > 0 && (
        <Section tone="muted" className="py-14 sm:py-16">
          <Container>
            <SectionHeading title={`Mer innen ${catLabel.toLowerCase()}`}>
              <Button asChild variant="ghost" className="h-10 rounded-[10px] text-[14px] font-semibold">
                <Link to="/produkter">
                  Alle produkter <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </SectionHeading>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="related-products">
              {related.map((p) => (
                <ProductCard key={p.slug} product={p} compact />
              ))}
            </div>
          </Container>
        </Section>
      )}
    </div>
  );
}
